import React, { lazy, Suspense } from "react";

import { createBrowserRouter, Navigate } from "react-router-dom";

import ErrorFallback from "./components/ErrorFallback";
import RootRoute from "./routes/rootRoute";
import { isBuiltDomain, isPlatformDomain } from "./utils/helpers";

// Import only the components needed for initial load

// Lazy load all other route components
const Admin = lazy(
	() => import(/* webpackChunkName: "adminRoute" */ "./routes/adminRoute"),
);
const Authenticated = lazy(
	() =>
		import(
			/* webpackChunkName: "authenticatedRoute" */ "./routes/authenticatedRoute"
		),
);
const AuthCallback = lazy(
	() =>
		import(
			/* webpackChunkName: "authCallbackRoute" */ "./routes/authCallbackRoute"
		),
);
const IntegrationCallback = lazy(
	() =>
		import(
			/* webpackChunkName: "integrationCallbackRoute" */ "./routes/integrationCallbackRoute"
		),
);
const Auth = lazy(
	() => import(/* webpackChunkName: "authRoute" */ "./routes/authRoute"),
);
const Feedback = lazy(
	() =>
		import(/* webpackChunkName: "feedbackRoute" */ "./routes/feedbackRoute"),
);
const Settings = lazy(
	() =>
		import(/* webpackChunkName: "settingsRoute" */ "./routes/settingsRoute"),
);
const Unsubscribe = lazy(
	() =>
		import(
			/* webpackChunkName: "unsubscribeRoute" */ "./routes/unsubscribeRoute"
		),
);
const BillableExport = lazy(
	() =>
		import(
			/* webpackChunkName: "billableExportRoute" */ "./routes/billableExportRoute"
		),
);
const BillingDashboard = lazy(
	() =>
		import(
			/* webpackChunkName: "billingDashboardRoute" */ "./routes/billingDashboardRoute"
		),
);
const PlaybookDoc = lazy(
	() =>
		import(
			/* webpackChunkName: "playbookDocRoute" */ "./routes/playbookDocRoute"
		),
);
const PlatformPlaybook = lazy(
	() =>
		import(
			/* webpackChunkName: "platformPlaybookRoute" */ "./routes/platformPlaybookRoute"
		),
);
const AcceptInvite = lazy(
	() =>
		import(
			/* webpackChunkName: "acceptInviteRoute" */ "./routes/acceptInviteRoute"
		),
);

// Loading component to show while chunks are loading
const LoadingFallback = () => (
	<div className="flex items-center justify-center h-screen">
		<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
	</div>
);

const MfeRouteLoader = lazy(
	() =>
		import(/* webpackChunkName: "mfeRouteLoader" */ "./components/MfeLoader"),
);

const routes = {
	auth: {
		signin: "/signin",
		login: "/login",
		signup: "/signup",
		callback: "/auth/callback",
		oauthCallback: "/oauth/callback",
	},
	docs: {
		playbook: "/docs/draw-agent-playbook",
		platformPlaybook: "/docs/platform-playbook",
	},
	admin: {
		root: "/admin",
		enterprise: "/admin/enterprise",
		pulse: "/admin/pulse",
		workflows: "/admin/workflows",
		agentCoaching: "/admin/agent-coaching",
	},
	mfe: {
		admin: "/mfe-admin",
		draw: "/mfe-draw",
		desk: "/mfe-draw-desk",
		performanceDashboard: "/mfe-performance-dashboard",
		statementAnalyzer: "/mfe-statement-analyzer",
	},
} as const;

export const createBuiltRouter = () => {
	// Wrap component with Suspense
	const withSuspense = (Component: React.ComponentType<any>) => (
		<Suspense fallback={<LoadingFallback />}>
			<Component />
		</Suspense>
	);

	const routeConfig = [
		{
			path: "/",
			element: <RootRoute />,
			errorElement: <ErrorFallback />,
			children: [
				{
					errorElement: <ErrorFallback />,
					children: [
						// Auth Routes
						{
							path: routes.auth.signin,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.login,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.signup,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.callback,
							element: withSuspense(AuthCallback),
						},
						{
							path: routes.auth.oauthCallback,
							element: withSuspense(AuthCallback),
						},
						// Public docs routes
						{
							path: "/docs",
							element: <Navigate to="/docs/draw-agent-playbook" replace />,
						},
						{
							path: `${routes.docs.platformPlaybook}/*`,
							element: withSuspense(PlatformPlaybook),
						},
						// Accept-invite landing (public — the token is the authority)
						{
							path: "/accept-invite",
							element: withSuspense(AcceptInvite),
						},
						// Protected Routes with Authenticated wrapper
						{
							element: withSuspense(Authenticated),
							children: [
								// Draw Agent playbook: customer names and prompts, login only
								{
									path: routes.docs.playbook,
									element: withSuspense(PlaybookDoc),
								},
								// Redirect root to dashboard
								{
									path: "/",
									element: <Navigate to="/dashboard" replace />,
								},
								// Redirect /home to /dashboard (for built domain)
								{
									path: "/home",
									element: <Navigate to="/dashboard" replace />,
								},
								// Dashboard (Pulse main dashboard) - wildcard for MFE sub-routing
								{
									path: "/dashboard/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "pulse" };
									},
								},
								// Insights (Feedback dashboard) - wildcard for MFE sub-routing
								{
									path: "/feedback/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "feedback" };
									},
								},
								// Agent Coaching - wildcard for MFE sub-routing
								{
									path: "/agent-coaching/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "agent-coaching" };
									},
								},
								// SOP Accelerator - wildcard for MFE sub-routing
								{
									path: "/sop-dashboard/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "sop-dashboard" };
									},
								},
								// Draw Agents - wildcard for MFE sub-routing
								{
									path: "/draw-agents/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "draw-agents" };
									},
								},
								// Settings
								{
									path: "/settings/*",
									element: withSuspense(Settings),
								},
								{
									path: "/billing/export",
									element: withSuspense(BillableExport),
								},
								{
									path: "/billing/export/preview",
									element: withSuspense(BillableExport),
								},
								{
									path: "/billing/dashboard",
									element: withSuspense(BillingDashboard),
								},
							],
						},
						// 404 Route
						{
							path: "*",
							element: <ErrorFallback errorType="routeNotFound" />,
						},
					],
				},
			],
		},
	];

	return createBrowserRouter(routeConfig, {
		basename: "/app",
		future: {
			v7_skipActionErrorRevalidation: true,
			v7_normalizeFormMethod: true,
			v7_routeLoaderData: true,
			v7_relativeSplatPath: true,
			v7_fetcherPersist: true,
			v7_partialHydration: true,
		},
	});
};

export const createPlatformRouter = () => {
	// Wrap component with Suspense
	const withSuspense = (Component: React.ComponentType<any>) => (
		<Suspense fallback={<LoadingFallback />}>
			<Component />
		</Suspense>
	);

	const routeConfig = [
		{
			path: "/",
			element: <RootRoute />,
			errorElement: <ErrorFallback />,
			children: [
				{
					errorElement: <ErrorFallback />,
					children: [
						// Auth Routes
						{
							path: routes.auth.signin,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.login,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.signup,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.callback,
							element: withSuspense(AuthCallback),
						},
						{
							path: routes.auth.oauthCallback,
							element: withSuspense(AuthCallback),
						},
						// Public docs routes
						{
							path: "/docs",
							element: <Navigate to="/docs/draw-agent-playbook" replace />,
						},
						{
							path: `${routes.docs.platformPlaybook}/*`,
							element: withSuspense(PlatformPlaybook),
						},
						// Accept-invite landing (public — the token is the authority)
						{
							path: "/accept-invite",
							element: withSuspense(AcceptInvite),
						},
						// Protected Routes with Authenticated wrapper (for auth and admin checks)
						{
							element: withSuspense(Authenticated),
							children: [
								// Draw Agent playbook: customer names and prompts, login only
								{
									path: routes.docs.playbook,
									element: withSuspense(PlaybookDoc),
								},
								{
									path: "/",
									element: <Navigate to="/workflows" replace />,
								},
								// Redirect /home to /workflows (for platform domain)
								{
									path: "/home",
									element: <Navigate to="/workflows" replace />,
								},
								{
									path: "/integrations/github_app/*",
									element: withSuspense(IntegrationCallback),
								},
								{
									path: "/workflows",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								// Workflow builder routes (relative to /workflows basePath)
								{
									path: "/workflows/workflow/:workflowId",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/workflow/create",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/workflow/config/:workflowId",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/workflow/config/:workflowId/:configTab",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/workflow/config/:workflowId/:configTab/:subTab",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									// Runtime view rendering (PAAS-659). The workflow-builder MFE
									// owns `/workflow/:slug/view/:viewSlug` (see its App.tsx), but the
									// host only forwards paths listed here — so without this entry the
									// MFE route was unreachable on every hosted domain and
									// `mb view-serving` pointed at a URL that could only resolve on the
									// standalone dev server.
									path: "/workflows/workflow/:workflowSlug/view/:viewSlug",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/settings",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									// Wildcard so deep MFE settings sub-paths (e.g.
									// /workflows/settings/members/123/access) reach
									// the workflow-builder's internal router. A single
									// :settingsTab param only matched one segment.
									path: "/workflows/settings/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/run/:runId",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/dev/authorize",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/dev/assume",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/workflows/s/:shareToken",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "workflows" };
									},
								},
								{
									path: "/autopilots",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "autopilots" };
									},
								},
								{
									path: "/feedback",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "feedback" };
									},
								},
								{
									path: "/feedback/insights",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "feedback" };
									},
								},
								{
									path: "/feedback/*",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "feedback" };
									},
								},
								{
									path: "/traces",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "traces" };
									},
								},
								{
									path: "/evals",
									element: withSuspense(Admin),
									loader: () => {
										return { adminTab: "evals" };
									},
								},
								{
									path: "/admin/:adminTab",
									element: withSuspense(Admin),
								},
								{
									path: "/settings/*",
									element: withSuspense(Settings),
								},
								{
									path: "/billing/export",
									element: withSuspense(BillableExport),
								},
								{
									path: "/billing/dashboard",
									element: withSuspense(BillingDashboard),
								},
								{
									path: "/enterprise/:enterpriseId",
									element: withSuspense(Admin),
								},
								{
									path: "/enterprise/:enterpriseId/:adminTab",
									element: withSuspense(Admin),
								},
								{
									path: "/enterprise/:enterpriseId/:adminTab/:autopilotId",
									element: withSuspense(Admin),
								},
								{
									path: "/enterprise/:enterpriseId/:adminTab/:autopilotId/edit",
									element: withSuspense(Admin),
								},
								{
									path: "/enterprise/:enterpriseId/:adminTab/:autopilotId/new",
									element: withSuspense(Admin),
								},
								{
									path: "/:adminTab/:enterpriseId",
									element: withSuspense(Admin),
								},
								{
									path: "/:adminTab/run/:runId",
									element: withSuspense(Admin),
								},
							],
						},
						// 404 Route
						{
							path: "*",
							element: <ErrorFallback errorType="routeNotFound" />,
						},
					],
				},
			],
		},
	];

	return createBrowserRouter(routeConfig, {
		basename: "/app",
		future: {
			v7_skipActionErrorRevalidation: true,
			v7_normalizeFormMethod: true,
			v7_routeLoaderData: true,
			v7_relativeSplatPath: true,
			v7_fetcherPersist: true,
			v7_partialHydration: true,
		},
	});
};

export const createAppRouter = () => {
	// Wrap component with Suspense
	const withSuspense = (Component: React.ComponentType<any>) => (
		<Suspense fallback={<LoadingFallback />}>
			<Component />
		</Suspense>
	);

	// Use built router for built domains (built-dev.mightybot.ai, built.mightybot.ai)
	if (isBuiltDomain()) {
		return createBuiltRouter();
	}

	// Use platform router for platform domains (platform-dev.mightybot.ai, platform.mightybot.ai, hulk.mightybot.ai)
	// Platform domains use root-level paths (basename="/") instead of /app paths
	if (isPlatformDomain()) {
		return createPlatformRouter();
	}

	const routeConfig = [
		{
			path: "/",
			element: <RootRoute />,
			errorElement: <ErrorFallback />,
			children: [
				{
					errorElement: <ErrorFallback />,
					children: [
						// Auth Routes
						{
							path: routes.auth.signin,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.login,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.signup,
							element: withSuspense(Auth),
						},
						{
							path: routes.auth.callback,
							element: withSuspense(AuthCallback),
						},
						{
							path: routes.auth.oauthCallback,
							element: withSuspense(AuthCallback),
						},

						// Billable Export Preview (unauthenticated for testing)
						{
							path: "/billing/export/preview",
							element: withSuspense(BillableExport),
						},

						// Public docs routes
						{
							path: "/docs",
							element: <Navigate to="/docs/draw-agent-playbook" replace />,
						},
						{
							path: `${routes.docs.platformPlaybook}/*`,
							element: withSuspense(PlatformPlaybook),
						},
						// Accept-invite landing (public — the token is the authority)
						{
							path: "/accept-invite",
							element: withSuspense(AcceptInvite),
						},

						// Protected Routes with Authenticated wrapper
						{
							element: withSuspense(Authenticated),
							children: [
								// Draw Agent playbook: customer names and prompts, login only
								{
									path: routes.docs.playbook,
									element: withSuspense(PlaybookDoc),
								},
								// Default redirect (consumer app decommissioned, /settings is the landing page)
								{
									path: "/",
									element: <Navigate to="/settings/general" replace />,
								},
								// Redirect legacy /home to /settings
								{
									path: "/home",
									element: <Navigate to="/settings/general" replace />,
								},
								// Settings
								{
									path: "/settings/*",
									element: withSuspense(Settings),
								},
								{
									path: "/billing/dashboard",
									element: withSuspense(BillingDashboard),
								},

								// Admin Routes
								{
									path: routes.admin.root,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab`,
									element: withSuspense(Admin),
									loader: ({ params }: { params: { adminTab: string } }) => {
										// Only allow pulse and agent-coaching tabs in Built environments
										const isBuiltEnvironment =
											process.env.REACT_APP_ENVIRONMENT === "built" ||
											process.env.REACT_APP_ENVIRONMENT === "built-dev" ||
											process.env.REACT_APP_ENVIRONMENT === "built-preprod" ||
											process.env.REACT_APP_ENVIRONMENT === "built-dr";
										if (
											(params.adminTab === "pulse" ||
												params.adminTab === "agent-coaching") &&
											!isBuiltEnvironment
										) {
											throw new Response("Not Found", { status: 404 });
										}
										return null;
									},
								},
								{
									path: `${routes.admin.root}/enterprise`,
									element: <Navigate to="/admin/enterprises" replace />,
								},
								{
									path: `${routes.admin.root}/:adminTab/:enterpriseId`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/enterprise/:enterpriseId/:adminTab/:autopilotId`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/enterprise/:enterpriseId/:adminTab/:autopilotId/edit`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/enterprise/:enterpriseId/:adminTab/:autopilotId/new`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/enterprise/:enterpriseId/autopilot/:autopilotId/:adminTab`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/insights/feedback/:insightId`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/deployments/:deploymentId`,
									element: withSuspense(Admin),
								},
								// workflow builder routes
								{
									// Deep MFE settings sub-paths (e.g.
									// /admin/workflows/settings/members/123/access).
									// :adminTab alone only matches one segment after
									// /admin/workflows.
									path: `${routes.admin.root}/:adminTab/settings`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/settings/*`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/workflow/:workflowId`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/workflow/:workflowId/:configTab`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/workflow/:workflowId/:configTab/:subTab`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/workflow/:workflowId/:configTab/:subTab/:subSubTab`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/run/:runId`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/dev/authorize`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/dev/authorize`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/dev/assume`,
									element: withSuspense(Admin),
								},
								{
									path: `${routes.admin.root}/:adminTab/agent-coaching`,
									element: withSuspense(Admin),
								},
								// Utility Routes
								{
									path: "/email/unsubscribe",
									element: withSuspense(Unsubscribe),
								},
								{
									path: "/email/feedback",
									element: withSuspense(Feedback),
								},
							],
						},

						// 404 Route
						{
							path: "*",
							element: <ErrorFallback errorType="routeNotFound" />,
						},
					],
				},
			],
		},
		// MFE Routes
		{
			path: routes.mfe.admin,
			element: (
				<MfeRouteLoader
					mfeName="admin"
					mfeUrl={`${process.env.REACT_APP_MFE_BASE_URL || "https://built-mfe.mightybot.ai"}/@mighty/draw-app/admin.js`}
					mfePath="@mighty/admin"
					activeWhen={["/app/mfe-admin"]}
					loadCss={false}
				/>
			),
		},
		{
			path: routes.mfe.draw,
			element: (
				<MfeRouteLoader
					mfeName="draw"
					mfeUrl={`${process.env.REACT_APP_MFE_BASE_URL || "https://built-mfe-dev.mightybot.ai"}/@mighty/draw-app/draw.js`}
					mfePath="@mighty/draw-app"
					activeWhen={["/app/mfe-draw"]}
					importMap={{
						"react-pdf~5.7.2":
							"https://built-mfe-dev.mightybot.ai/cdn/react-pdf~5.7.2.js",
						"react~18.1.0":
							"https://unpkg.com/react@18/umd/react.development.js",
						"react-dom~18.1.0":
							"https://unpkg.com/react-dom@18/umd/react-dom.development.js",
					}}
					loadCss={false}
				/>
			),
		},
		{
			path: routes.mfe.desk,
			element: (
				<MfeRouteLoader
					mfeName="draw-desk"
					mfeUrl={`${process.env.REACT_APP_MFE_BASE_URL || "https://built-mfe-dev.mightybot.ai"}/@mighty/draw-app/draw-desk.js`}
					mfePath="@mighty/draw-desk"
					activeWhen={["/app/mfe-draw-desk"]}
					loadCss={false}
				/>
			),
		},
		{
			path: routes.mfe.performanceDashboard,
			element: (
				<MfeRouteLoader
					mfeName="performance-dashboard"
					baseUrl={
						process.env.REACT_APP_MFE_BASE_URL ||
						"https://built-mfe-dev.mightybot.ai"
					}
					mfePath="@mighty/draw-app"
				/>
			),
		},
		{
			path: routes.mfe.statementAnalyzer,
			element: (
				<MfeRouteLoader
					mfeName="statement-analyzer"
					baseUrl="https://statement-analyser-mfe-dev.s3.us-east-1.amazonaws.com"
					mfePath="@mighty"
				/>
			),
		},
	];

	return createBrowserRouter(routeConfig, {
		basename: "/app",
		future: {
			v7_skipActionErrorRevalidation: true,
			v7_normalizeFormMethod: true,
			v7_routeLoaderData: true,
			v7_relativeSplatPath: true,
			v7_fetcherPersist: true,
			v7_partialHydration: true,
		},
	});
};
